import { useFieldArray } from 'react-hook-form';
import * as AlertNotification from '@givebutter/primitives/alert-notification';
import { Button } from '@givebutter/primitives/button';
import { faCircleMinus, faCirclePlus } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as Form from 'components/form';
import { useGivebutterFormContext } from 'components/form';
import { InputCurrencyIcon } from 'components/input';
import * as Tooltip from 'components/tooltip';
import { CampaignDonationFrequency, DonationAmountValue } from 'types/domain';

type DonationAmountFormValue = Omit<DonationAmountValue, 'value'> & {
    value: string;
};

interface CustomDonationFormProps {
    once?: DonationAmountFormValue[];
    monthly?: DonationAmountFormValue[];
    quarterly?: DonationAmountFormValue[];
    yearly?: DonationAmountFormValue[];
    requireMinimumDonationAmount: boolean;
    showOtherAmountField: boolean;
    minimumDonationAmount?: string;
    showCustomRecurringDonationAmounts: boolean;
    activeTab: CampaignDonationFrequency;
}

interface CustomAmountFormType {
    type: CampaignDonationFrequency;
    disabled?: boolean;
}

const MAX_DONATIONS_AMOUNTS = 18;
const defaultAmountAppendValue = { value: '', description: '' };

const CustomDonationAmountForm = ({ type, disabled }: CustomAmountFormType) => {
    const { control, path } = useGivebutterFormContext<CustomDonationFormProps>();
    const amountsFieldArray = useFieldArray({
        name: type,
        control,
    });
    const { fields: amountFields, append, remove } = amountsFieldArray;

    const hasReachedLimit = amountFields.length >= MAX_DONATIONS_AMOUNTS;

    if (!amountFields.length) {
        return (
            <div className="flex flex-col items-center gap-5 bg-gray-100 p-6">
                <div className="flex flex-col items-center">
                    <p className="font-medium">You have no custom amounts</p>
                    <p>Get started by adding a custom donation amount</p>
                </div>
                <div className="flex flex-col items-center">
                    <Button
                        variant="outline"
                        color="neutral"
                        onClick={() => append(defaultAmountAppendValue)}
                    >
                        Add custom amount
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-y-4">
            {disabled && (
                <AlertNotification.Root type={'error'}>
                    <AlertNotification.Icon />
                    <div className={'flex flex-col gap-3'}>
                        <AlertNotification.Content>
                            <AlertNotification.Description>
                                This frequency is disabled
                            </AlertNotification.Description>
                        </AlertNotification.Content>
                    </div>
                </AlertNotification.Root>
            )}
            {amountFields?.map((field, index) => (
                <div key={field.id} className="flex flex-col gap-y-2">
                    <div key={field.id} className={'grid grid-cols-6 gap-3'}>
                        <div className="col-span-2">
                            <Form.Container>
                                {index === 0 && (
                                    <Form.Label name={path(`${type}.${index}.value`)}>
                                        Amount
                                    </Form.Label>
                                )}
                                <Form.TextInputRoot
                                    control={control}
                                    name={`${type}.${index}.value`}
                                    rules={{ required: 'Amount is required.' }}
                                    inputRootProps={{
                                        mask: 'decimal',
                                    }}
                                >
                                    <InputCurrencyIcon />
                                    <Form.TextInputForm placeholder={'0.00'} />
                                </Form.TextInputRoot>
                                <Form.UseFormError name={path(`${type}.${index}.value`)} />
                            </Form.Container>
                        </div>
                        <div className="col-span-3">
                            <Form.Container>
                                {index === 0 && (
                                    <Form.Label name={path(`${type}.${index}.description`)}>
                                        Description
                                    </Form.Label>
                                )}
                                <Form.TextInputRoot
                                    control={control}
                                    name={`${type}.${index}.description`}
                                >
                                    <Form.TextInputForm placeholder={'Description (optional)'} />
                                </Form.TextInputRoot>
                                <Form.UseFormError name={path(`${type}.${index}.description`)} />
                            </Form.Container>
                        </div>
                        <div className="col-span-1 flex flex-row items-center">
                            <Button
                                onClick={() => remove(index)}
                                size="sm"
                                variant="transparent"
                                shape={'square'}
                                color="error"
                                className={'bottom-1 self-end'}
                                aria-label="Remove"
                            >
                                <FontAwesomeIcon icon={faCircleMinus} />
                            </Button>
                            {index === amountFields.length - 1 && (
                                <Tooltip.Root open={hasReachedLimit ? undefined : false}>
                                    <Tooltip.Trigger asChild>
                                        <Button
                                            onClick={() => append(defaultAmountAppendValue)}
                                            size="sm"
                                            variant="transparent"
                                            shape={'square'}
                                            color="primary"
                                            disabled={hasReachedLimit}
                                            aria-label="Add"
                                        >
                                            <FontAwesomeIcon icon={faCirclePlus} />
                                        </Button>
                                    </Tooltip.Trigger>
                                    <Tooltip.Portal>
                                        <Tooltip.Content inverse={true}>
                                            <p>
                                                Limit reached. You can only add up to{' '}
                                                {MAX_DONATIONS_AMOUNTS} different custom amounts.
                                            </p>
                                            <Tooltip.Arrow inverse={true} />
                                        </Tooltip.Content>
                                    </Tooltip.Portal>
                                </Tooltip.Root>
                            )}
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );
};

export { CustomDonationAmountForm };
